import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { adminApi } from "@/lib/adminApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Search, Filter, Ban, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const KYC = ["all", "pending", "approved", "rejected"];
const TIER = ["all", "new", "verified", "trusted"];
const STATUS = ["all", "active", "suspended"];
const ROLE = ["all", "sender", "carrier", "both"];

const badge = (txt, tone) => <Badge className={`text-[10px] ${tone}`}>{txt}</Badge>;

const Users = () => {
  const [sp, setSp] = useSearchParams();
  const [q, setQ] = useState(sp.get("q") || "");
  const [role_filter, setRoleFilter] = useState(sp.get("role_filter") || "all");
  const [kyc_status, setKyc] = useState(sp.get("kyc_status") || "all");
  const [trust_tier, setTier] = useState(sp.get("trust_tier") || "all");
  const [status, setStatus] = useState(sp.get("status") || "all");
  const [signup_from, setFrom] = useState(sp.get("signup_from") || "");
  const [signup_to, setTo] = useState(sp.get("signup_to") || "");
  const [sort, setSort] = useState(sp.get("sort") || "-created_at");
  const [page, setPage] = useState(parseInt(sp.get("page") || "1", 10));

  const [data, setData] = useState({ items: [], total: 0, page: 1, page_size: 50 });
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = { q, role_filter, kyc_status, trust_tier, status, signup_from, signup_to, sort, page, page_size: 50 };
      Object.keys(params).forEach(k => (params[k] === "" || params[k] === "all") && delete params[k]);
      const r = await adminApi.get("/users", { params });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const next = { q, role_filter, kyc_status, trust_tier, status, signup_from, signup_to, sort, page };
    Object.keys(next).forEach(k => (next[k] === "" || next[k] === "all") && delete next[k]);
    setSp(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, role_filter, kyc_status, trust_tier, status, signup_from, signup_to, sort, page]);

  const totalPages = useMemo(() => Math.max(1, Math.ceil((data.total || 0) / (data.page_size || 50))), [data]);
  const toggleSort = (field) => {
    setSort(sort === field ? "-" + field : field);
  };

  return (
    <div className="p-8">
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900">Users</h1>
          <p className="text-sm text-slate-500 mt-1">All senders and carriers on the platform.</p>
        </div>
      </div>

      <div className="rounded-2xl bg-white border border-slate-200 p-4 mb-4 flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[220px]">
          <label className="text-[11px] uppercase tracking-wider text-slate-500">Search</label>
          <div className="relative mt-1">
            <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-slate-400"/>
            <Input data-testid="users-search" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Name, phone or email" className="pl-8"/>
          </div>
        </div>
        <Filter5 label="Role" value={role_filter} setValue={(v) => { setRoleFilter(v); setPage(1); }} options={ROLE} testid="filter-role"/>
        <Filter5 label="KYC" value={kyc_status} setValue={(v) => { setKyc(v); setPage(1); }} options={KYC} testid="filter-kyc"/>
        <Filter5 label="Trust tier" value={trust_tier} setValue={(v) => { setTier(v); setPage(1); }} options={TIER} testid="filter-tier"/>
        <Filter5 label="Status" value={status} setValue={(v) => { setStatus(v); setPage(1); }} options={STATUS} testid="filter-status"/>
        <div>
          <label className="text-[11px] uppercase tracking-wider text-slate-500">Signup from</label>
          <Input data-testid="filter-signup-from" type="date" value={signup_from} onChange={(e) => { setFrom(e.target.value); setPage(1); }} className="mt-1"/>
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-wider text-slate-500">Signup to</label>
          <Input data-testid="filter-signup-to" type="date" value={signup_to} onChange={(e) => { setTo(e.target.value); setPage(1); }} className="mt-1"/>
        </div>
      </div>

      <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden">
        <div className="px-4 py-2 text-xs text-slate-500 border-b border-slate-100 flex items-center justify-between">
          <span data-testid="users-total">{data.total} users</span>
          <span>Page {page} of {totalPages}</span>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
            <tr>
              <Th onClick={() => toggleSort("name")} active={sort.replace(/^-/, "") === "name"}>Name</Th>
              <th className="px-4 py-2 text-left">Phone</th>
              <th className="px-4 py-2 text-left">Role</th>
              <Th onClick={() => toggleSort("created_at")} active={sort.replace(/^-/, "") === "created_at"}>Signup</Th>
              <Th onClick={() => toggleSort("delivery_count")} active={sort.replace(/^-/, "") === "delivery_count"}>Deliveries</Th>
              <Th onClick={() => toggleSort("rating_avg")} active={sort.replace(/^-/, "") === "rating_avg"}>Rating</Th>
              <th className="px-4 py-2 text-left">KYC</th>
              <th className="px-4 py-2 text-left">Tier</th>
              <th className="px-4 py-2 text-left">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {data.items.map(u => (
              <tr key={u.id} data-testid={`user-row-${u.id}`} className="hover:bg-slate-50 cursor-pointer">
                <td className="px-4 py-3">
                  <Link to={`/admin/users/${u.id}`} className="text-slate-900 font-medium hover:underline">
                    {u.name || <span className="italic text-slate-400">Unnamed</span>}
                  </Link>
                  {u.email && <div className="text-[11px] text-slate-500">{u.email}</div>}
                </td>
                <td className="px-4 py-3 text-slate-600">{u.phone}</td>
                <td className="px-4 py-3">
                  {u.derived_role === "both" && badge("BOTH", "bg-violet-100 text-violet-700")}
                  {u.derived_role === "carrier" && badge("CARRIER", "bg-emerald-100 text-emerald-700")}
                  {u.derived_role === "sender" && badge("SENDER", "bg-blue-100 text-blue-700")}
                  {u.derived_role === "none" && <span className="text-slate-400 text-xs">—</span>}
                </td>
                <td className="px-4 py-3 text-slate-600 tabular-nums text-xs">
                  {u.created_at ? new Date(u.created_at).toLocaleDateString() : ""}
                </td>
                <td className="px-4 py-3 tabular-nums">{u.delivery_count || 0}</td>
                <td className="px-4 py-3 tabular-nums">{u.rating_avg ? u.rating_avg.toFixed(1) : "—"}</td>
                <td className="px-4 py-3">
                  {u.kyc_status === "approved" && badge("APPROVED", "bg-emerald-100 text-emerald-700")}
                  {u.kyc_status === "pending" && badge("PENDING", "bg-amber-100 text-amber-700")}
                  {u.kyc_status === "rejected" && badge("REJECTED", "bg-red-100 text-red-700")}
                </td>
                <td className="px-4 py-3">
                  {u.trust_tier === "trusted" && badge("TRUSTED", "bg-slate-900 text-white")}
                  {u.trust_tier === "verified" && badge("VERIFIED", "bg-slate-700 text-white")}
                  {(!u.trust_tier || u.trust_tier === "new") && badge("NEW", "bg-slate-200 text-slate-700")}
                </td>
                <td className="px-4 py-3">
                  {u.suspended
                    ? <span className="inline-flex items-center gap-1 text-red-700 text-xs"><Ban className="w-3 h-3"/> Suspended</span>
                    : <span className="inline-flex items-center gap-1 text-emerald-700 text-xs"><CheckCircle2 className="w-3 h-3"/> Active</span>}
                </td>
              </tr>
            ))}
            {!loading && data.items.length === 0 && (
              <tr><td colSpan={9} className="text-center py-10 text-sm text-slate-400">No users match these filters.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1} data-testid="pg-prev">← Prev</Button>
        <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={page >= totalPages} data-testid="pg-next">Next →</Button>
      </div>
    </div>
  );
};

const Th = ({ children, onClick, active }) => (
  <th onClick={onClick} className="px-4 py-2 text-left cursor-pointer select-none hover:text-slate-900">
    {children} {active && <span className="text-slate-400 text-[9px]">▼</span>}
  </th>
);

const Filter5 = ({ label, value, setValue, options, testid }) => (
  <div>
    <label className="text-[11px] uppercase tracking-wider text-slate-500">{label}</label>
    <Select value={value} onValueChange={setValue}>
      <SelectTrigger data-testid={testid} className="mt-1 w-[140px]"><SelectValue/></SelectTrigger>
      <SelectContent>
        {options.map(o => <SelectItem key={o} value={o}>{o === "all" ? "All" : o.replace(/_/g, " ")}</SelectItem>)}
      </SelectContent>
    </Select>
  </div>
);

export default Users;
